import { useState } from "react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { MainLayout } from "../components/layout";
import api from "../services/api";

interface TableData {
  columns: string[];
  rows: any[][];
}

export function DbViewer() {
  const [selectedTable, setSelectedTable] = useState<string>("");
  const [tableData, setTableData] = useState<TableData | null>(null);
  const [tables, setTables] = useState<string[]>([]);
  const [loading, setLoading] = useState<string>("");
  const [error, setError] = useState<string>("");

  const fetchTables = async () => {
    setLoading("tables");
    setError("");
    try {
      const response = await api.get("/api/v1/debug/tables");
      setTables(response.data.data.tables || []);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || "Failed to fetch tables");
    } finally {
      setLoading("");
    }
  };

  const fetchTableData = async (tableName: string) => {
    setLoading(tableName);
    setError("");
    setSelectedTable(tableName);
    try {
      const response = await api.get(`/api/v1/debug/tables/${tableName}`);
      setTableData(response.data.data);
    } catch (err: any) {
      setError(
        err.response?.data?.error?.message || "Failed to fetch table data"
      );
      setTableData(null);
    } finally {
      setLoading("");
    }
  };

  const quickQueries = [
    {
      name: "All Users",
      table: "users",
      query: "SELECT id, email, first_name, last_name, role, status FROM users",
    },
    {
      name: "All Departments",
      table: "departments",
      query: "SELECT * FROM departments",
    },
    {
      name: "All Sessions",
      table: "sessions",
      query: "SELECT * FROM sessions ORDER BY created_at DESC LIMIT 10",
    },
  ];

  return (
    <MainLayout title="Database Viewer">
      <div className="max-w-7xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Database Viewer</CardTitle>
            <CardDescription>
              Inspect local D1 database tables (Development only)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Quick Actions */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Quick Actions</h3>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={fetchTables} disabled={loading === "tables"}>
                  {loading === "tables" ? "Loading..." : "List All Tables"}
                </Button>
                {quickQueries.map((q) => (
                  <Button
                    key={q.name}
                    variant="outline"
                    onClick={() => fetchTableData(q.table)}
                    disabled={loading === q.table}
                  >
                    {loading === q.table ? "Loading..." : q.name}
                  </Button>
                ))}
              </div>
            </div>

            {/* Tables List */}
            {tables.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-3">
                  Available Tables ({tables.length})
                </h3>
                <div className="grid grid-cols-4 gap-2">
                  {tables.map((table) => (
                    <Button
                      key={table}
                      variant={selectedTable === table ? "default" : "outline"}
                      size="sm"
                      onClick={() => fetchTableData(table)}
                      disabled={loading === table}
                      className="text-xs"
                    >
                      {loading === table ? "..." : table}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Table Data */}
            {tableData && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold">
                    Table: {selectedTable}
                  </h3>
                  <span className="text-xs text-gray-500">
                    {tableData.rows.length} rows
                  </span>
                </div>

                {tableData.rows.length === 0 ? (
                  <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg">
                    No data in this table
                  </div>
                ) : (
                  <div className="overflow-auto max-h-96 border rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          {tableData.columns.map((col, idx) => (
                            <th
                              key={idx}
                              className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
                            >
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {tableData.rows.map((row, rowIdx) => (
                          <tr key={rowIdx} className="hover:bg-gray-50">
                            {row.map((cell, cellIdx) => (
                              <td
                                key={cellIdx}
                                className="px-4 py-2 text-xs text-gray-900 whitespace-nowrap"
                              >
                                {cell === null ? (
                                  <span className="text-gray-400 italic">
                                    null
                                  </span>
                                ) : typeof cell === "boolean" ? (
                                  <span
                                    className={
                                      cell ? "text-green-600" : "text-red-600"
                                    }
                                  >
                                    {cell.toString()}
                                  </span>
                                ) : (
                                  String(cell)
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Instructions */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h3 className="font-semibold text-blue-900 mb-2">How to use:</h3>
              <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
                <li>
                  Click "List All Tables" to see all available database tables
                </li>
                <li>Use quick action buttons to view common tables</li>
                <li>Click any table name to view its contents</li>
                <li>
                  Note: This requires debug endpoints enabled in the API (dev
                  only)
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}

export default DbViewer;
